import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Skeleton from "@material-ui/lab/Skeleton";
import { green, orange, red, grey } from "@material-ui/core/colors";

import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import LinkIcon from "@material-ui/icons/Link";
import SendIcon from "@material-ui/icons/Send";
import WifiIcon from "@material-ui/icons/Wifi";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import ReceiptIcon from "@material-ui/icons/Receipt";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import BusinessIcon from "@material-ui/icons/Business";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";

import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import ErpWritebackDialog from "./ErpWritebackDialog";
import { t } from "./i18n";

/**
 * Painel ERP do contato (AtendeISP): vincular ao cadastro SGP/IXC e, quando vinculado,
 * ver contratos, conexão, faturas em aberto e disparar ações no ticket.
 * Props: contact (obrigatório), ticket (opcional — sem ele, ações de envio ficam desabilitadas).
 */

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0)
  },
  card: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column"
  },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    "& svg": { fontSize: 18, color: theme.palette.primary.main }
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  line: {
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: "break-word"
  },
  contractItem: {
    padding: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
    display: "flex",
    flexDirection: "column",
    gap: 2
  },
  contractHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4
  },
  statusChip: {
    height: 20,
    fontSize: 11,
    color: "#fff",
    fontWeight: 600
  },
  active: { backgroundColor: green[600] },
  suspended: { backgroundColor: orange[700] },
  blocked_manual: { backgroundColor: red[600] },
  cancelled: { backgroundColor: grey[600] },
  pending: { backgroundColor: grey[500] },
  unknown: { backgroundColor: grey[500] },
  connRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    fontSize: 13
  },
  online: { color: green[600] },
  offline: { color: red[600] },
  grp: {
    marginTop: 6
  },
  grpTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: 1,
    marginBottom: 2
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "96px 1fr",
    columnGap: 6,
    rowGap: 1,
    fontSize: 12
  },
  kvKey: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap"
  },
  kvVal: {
    wordBreak: "break-word"
  },
  table: {
    "& td, & th": {
      padding: "4px 4px",
      fontSize: 12,
      whiteSpace: "nowrap"
    }
  },
  tableWrap: {
    overflowX: "auto"
  },
  overdue: { color: red[600], fontWeight: 600 },
  upcoming: { color: green[700], fontWeight: 600 },
  rowActions: {
    display: "flex",
    gap: 2
  },
  actionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5)
  },
  resultCustomer: {
    padding: theme.spacing(0.75),
    marginTop: theme.spacing(0.5)
  },
  footer: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    textAlign: "right",
    paddingRight: 4
  },
  error: {
    fontSize: 12,
    color: theme.palette.error.main
  },
  warn: {
    fontSize: 12,
    color: orange[800]
  }
}));

const onlyDigits = v => String(v || "").replace(/\D/g, "");

const formatCpfCnpj = v => {
  const d = onlyDigits(v);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "";
};

const formatCurrency = v => {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return "-";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v));
};

const formatDate = iso => {
  if (!iso) return "-";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const formatDateTime = iso => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Faixas usuais de RX em GPON: até -25 dBm bom; -25 a -28 atenção; abaixo de -28 crítico.
const signalClass = (rx, classes) => {
  const v = Number(rx);
  if (Number.isNaN(v)) return "";
  if (v > -25) return classes.online;
  if (v >= -28) return classes.warn;
  return classes.error;
};

const signalLabel = rx => {
  const v = Number(rx);
  if (Number.isNaN(v)) return "";
  if (v > -25) return t("signalGood");
  if (v >= -28) return t("signalWarn");
  return t("signalBad");
};

/** Grupo rotulado de pares chave→valor; some sozinho quando nenhum valor existe. */
const KVGroup = ({ title, items, classes }) => {
  const rows = (items || []).filter(it => it && it.v !== undefined && it.v !== null && it.v !== "");
  if (!rows.length) return null;
  return (
    <div className={classes.grp}>
      {title && <Typography className={classes.grpTitle}>{title}</Typography>}
      <div className={classes.kvGrid}>
        {rows.map((it, i) => (
          <React.Fragment key={i}>
            <span className={classes.kvKey}>{it.k}</span>
            <span className={classes.kvVal}>{it.v}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const formatBytes = bytes => {
  if (bytes === undefined || bytes === null || Number.isNaN(Number(bytes))) return "-";
  const b = Number(bytes);
  const gb = b / 1024 ** 3;
  if (gb >= 1) return `${gb.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} GB`;
  const mb = b / 1024 ** 2;
  if (mb >= 1) return `${mb.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
  return `${Math.round(b / 1024)} KB`;
};

const formatUptime = seconds => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  if (total < 60) return "< 1 min";
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  return [d ? `${d}d` : null, h ? `${h}h` : null, m ? `${m}min` : null].filter(Boolean).join(" ");
};

const formatTime = iso => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const formatAddress = a => {
  if (!a) return "";
  const parts = [
    [a.street, a.number].filter(Boolean).join(", "),
    a.district,
    [a.city, a.state].filter(Boolean).join("/")
  ].filter(Boolean);
  return parts.join(" - ");
};

const statusLabelKey = {
  active: "statusActive",
  suspended: "statusSuspended",
  blocked_manual: "statusBlocked",
  cancelled: "statusCancelled",
  pending: "statusPending",
  unknown: "statusUnknown"
};

const isOverdue = inv => {
  if (inv.status === "overdue") return true;
  if (!inv.dueDate) return false;
  const today = new Date().toISOString().slice(0, 10);
  return String(inv.dueDate).slice(0, 10) < today;
};

const findContactCpf = contact => {
  const infos = (contact && contact.extraInfo) || [];
  const hit = infos.find(i => /cpf|cnpj/i.test(String(i.name || "")));
  return hit ? formatCpfCnpj(hit.value) : "";
};

const StatusChip = ({ status, rawStatus }) => {
  const classes = useStyles();
  const key = statusLabelKey[status] ? status : "unknown";
  const label = t(statusLabelKey[key]);
  return (
    <Tooltip title={rawStatus || ""}>
      <Chip size="small" label={label} className={`${classes.statusChip} ${classes[key]}`} />
    </Tooltip>
  );
};

const CardTitle = ({ icon, children, action }) => {
  const classes = useStyles();
  return (
    <div className={classes.headerRow}>
      <Typography variant="subtitle2" className={classes.cardTitle}>
        {icon}
        {children}
      </Typography>
      {action}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bloco "Vincular ao ERP"
// ---------------------------------------------------------------------------
const LinkForm = ({ contact, integrations, onLinked }) => {
  const classes = useStyles();
  const [integrationId, setIntegrationId] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState(() => findContactCpf(contact));
  const [password, setPassword] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [linking, setLinking] = useState("");

  useEffect(() => {
    if (integrations.length && !integrationId) setIntegrationId(String(integrations[0].id));
  }, [integrations, integrationId]);

  const selected = integrations.find(i => String(i.id) === String(integrationId));
  const isSgp = selected && selected.type === "SGP";

  const handleSearch = async e => {
    if (e) e.preventDefault();
    if (!onlyDigits(cpfCnpj)) return;
    setSearching(true);
    setResults(null);
    try {
      const { data } = await api.post(`/isp/contacts/${contact.id}/erp/search`, {
        cpfCnpj: onlyDigits(cpfCnpj),
        erpIntegrationId: integrationId ? Number(integrationId) : undefined,
        password: password || undefined
      });
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async (customer, contract) => {
    const key = `${customer.id}:${contract ? contract.id : ""}`;
    setLinking(key);
    try {
      const { data } = await api.post(`/isp/contacts/${contact.id}/erp/link`, {
        erpIntegrationId: Number(integrationId),
        erpCustomerId: customer.id,
        erpContractId: contract ? contract.id : undefined,
        cpfCnpj: onlyDigits(customer.cpfCnpj || cpfCnpj),
        customerName: customer.name,
        password: password || undefined
      });
      toast.success(t("linkedOk"));
      onLinked(data, password);
    } catch (err) {
      toastError(err);
    } finally {
      setLinking("");
    }
  };

  if (!integrations.length) {
    return (
      <Paper variant="outlined" className={classes.card}>
        <CardTitle icon={<LinkIcon />}>{t("linkTitle")}</CardTitle>
        <Typography className={classes.warn}>{t("noIntegration")}</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" className={classes.card}>
      <CardTitle icon={<LinkIcon />}>{t("linkTitle")}</CardTitle>
      <Typography className={classes.muted}>{t("linkHint")}</Typography>
      <form className={classes.form} onSubmit={handleSearch}>
        {integrations.length > 1 && (
          <TextField
            select
            size="small"
            variant="outlined"
            label={t("integration")}
            value={integrationId}
            onChange={e => setIntegrationId(e.target.value)}
          >
            {integrations.map(i => (
              <MenuItem key={i.id} value={String(i.id)}>
                {i.name} ({i.type})
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          size="small"
          variant="outlined"
          label={t("cpfCnpj")}
          value={cpfCnpj}
          onChange={e => setCpfCnpj(e.target.value)}
          inputProps={{ inputMode: "numeric" }}
        />
        {isSgp && (
          <TextField
            size="small"
            variant="outlined"
            type="password"
            label={t("password")}
            helperText={t("passwordHint")}
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="small"
          disabled={searching || !onlyDigits(cpfCnpj)}
          startIcon={searching ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
        >
          {searching ? t("searching") : t("search")}
        </Button>
      </form>

      {results && results.length === 0 && <Typography className={classes.muted} style={{ marginTop: 8 }}>{t("noResults")}</Typography>}

      {results &&
        results.map(customer => (
          <Paper key={customer.id} variant="outlined" className={classes.resultCustomer}>
            <Typography className={classes.line} style={{ fontWeight: 600 }}>
              {customer.name || "-"}
            </Typography>
            <Typography className={classes.muted}>{formatCpfCnpj(customer.cpfCnpj || cpfCnpj)}</Typography>
            {(customer.contracts || []).length === 0 && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                style={{ marginTop: 4 }}
                disabled={!!linking}
                onClick={() => handleLink(customer)}
              >
                {t("linkCustomer")}
              </Button>
            )}
            {(customer.contracts || []).map(ct => (
              <div key={ct.id} className={classes.contractItem} style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <div className={classes.contractHead}>
                  <Typography className={classes.line}>
                    {t("contract")} {ct.code || ct.id}
                  </Typography>
                  <StatusChip status={ct.status} rawStatus={ct.rawStatus} />
                </div>
                {ct.planName && (
                  <Typography className={classes.muted}>
                    {ct.planName}
                    {ct.planValue !== undefined ? ` · ${formatCurrency(ct.planValue)}` : ""}
                  </Typography>
                )}
                {ct.installAddress && <Typography className={classes.muted}>{formatAddress(ct.installAddress)}</Typography>}
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={!!linking}
                  startIcon={linking === `${customer.id}:${ct.id}` ? <CircularProgress size={12} color="inherit" /> : <LinkIcon />}
                  onClick={() => handleLink(customer, ct)}
                >
                  {t("linkButton")}
                </Button>
              </div>
            ))}
          </Paper>
        ))}
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Visão do cliente vinculado
// ---------------------------------------------------------------------------
const LinkedView = ({ contact, ticket, overview, onRefresh, onUnlinked, refreshing, password, setPassword }) => {
  const classes = useStyles();
  const [busy, setBusy] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [confirmUnblock, setConfirmUnblock] = useState(null);
  const [writebackOpen, setWritebackOpen] = useState(false);

  const { link, customer, contracts = [], invoices = [], connection, connectionError, invoicesError, customerError } = overview;
  const canSend = !!(ticket && ticket.id);
  // Cobrança manual: encerra o atendimento depois de enviar (padrão ligado)
  const [closeAfter, setCloseAfter] = useState(true);
  const unblockable = contracts.filter(c => c.status === "suspended" || c.status === "blocked_manual");

  const callTicket = async (path, body, key) => {
    if (!canSend) {
      toast.warn(t("noTicket"));
      return;
    }
    setBusy(key);
    try {
      const { data } = await api.post(`/isp/tickets/${ticket.id}/erp/${path}`, { ...(body || {}), closeAfter: closeAfter && (path === "send-invoice" || path === "send-all-invoices"), password: password || undefined });
      toast.success(data && data.closed ? t("sentAndClosed") : t("sent"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy("");
    }
  };

  const handleUnlink = async () => {
    setBusy("unlink");
    try {
      await api.delete(`/isp/contacts/${contact.id}/erp/link`);
      toast.success(t("unlinkedOk"));
      onUnlinked();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy("");
    }
  };

  const handleUnblock = async contractId => {
    setBusy("unblock");
    try {
      const { data } = await api.post(`/isp/contacts/${contact.id}/erp/trust-unblock`, { contractId, password: password || undefined });
      if (data && data.ok) toast.success(data.message || t("unblockOk"));
      else toast.warn((data && data.message) || t("unblockOk"));
      onRefresh();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy("");
    }
  };

  const connectionContract = useMemo(
    () => contracts.find(c => connection && String(c.id) === String(connection.contractId)),
    [contracts, connection]
  );

  return (
    <>
      {/* Cabeçalho do cliente */}
      <Paper variant="outlined" className={classes.card}>
        <div className={classes.headerRow}>
          <div style={{ minWidth: 0 }}>
            <Typography className={classes.line} style={{ fontWeight: 600 }} noWrap>
              {(customer && customer.name) || link.customerName || "-"}
            </Typography>
            <Typography className={classes.muted}>
              {link.cpfCnpjMasked} · {overview.providerLabel}
              {link.integrationName ? ` · ${link.integrationName}` : ""}
            </Typography>
          </div>
          <div className={classes.headerActions}>
            <Tooltip title={t("refresh")}>
              <span>
                <IconButton size="small" aria-label={t("refresh")} onClick={onRefresh} disabled={refreshing}>
                  {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("unlink")}>
              <span>
                <IconButton size="small" aria-label={t("unlink")} onClick={() => setConfirmUnlink(true)} disabled={busy === "unlink"}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        {overview.needsPassword && (
          <div className={classes.form}>
            <Typography className={classes.warn}>{t("needsPassword")}</Typography>
            <TextField
              size="small"
              variant="outlined"
              type="password"
              label={t("password")}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <Button size="small" variant="contained" color="primary" onClick={onRefresh} disabled={!password || refreshing}>
              {t("refresh")}
            </Button>
          </div>
        )}
        {customerError && !overview.needsPassword && <Typography className={classes.error}>{customerError}</Typography>}
      </Paper>

      {/* Contratos */}
      <Paper variant="outlined" className={classes.card}>
        <CardTitle icon={<BusinessIcon />}>{t("contracts")}</CardTitle>
        {contracts.length === 0 && <Typography className={classes.muted}>{t("noContracts")}</Typography>}
        {contracts.map(ct => (
          <Paper key={ct.id} variant="outlined" className={classes.contractItem}>
            <div className={classes.contractHead}>
              <Typography className={classes.line} style={{ fontWeight: 600 }}>
                {t("contract")} {ct.code || ct.id}
              </Typography>
              <StatusChip status={ct.status} rawStatus={ct.rawStatus} />
            </div>
            {(ct.planName || ct.planValue !== undefined) && (
              <Typography className={classes.line}>
                {ct.planName || t("plan")}
                {ct.planValue !== undefined ? ` · ${formatCurrency(ct.planValue)}` : ""}
              </Typography>
            )}
            {ct.installAddress && formatAddress(ct.installAddress) && (
              <Typography className={classes.muted}>{formatAddress(ct.installAddress)}</Typography>
            )}
          </Paper>
        ))}
      </Paper>

      {/* Conexão */}
      <Paper variant="outlined" className={classes.card}>
        <CardTitle
          icon={<WifiIcon />}
          action={
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AssignmentTurnedInIcon />}
                disabled={!canSend}
                onClick={() => setWritebackOpen(true)}
              >
                {t("writeback")}
              </Button>
              <Tooltip title={t("sendStatus")}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={t("sendStatus")}
                    disabled={!canSend || !connection || !!busy}
                    onClick={() => callTicket("send-connection-status", null, "status")}
                  >
                    {busy === "status" ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          }
        >
          {t("connection")}
        </CardTitle>
        {connection ? (
          <>
            <div className={`${classes.connRow} ${connection.online === undefined ? "" : connection.online ? classes.online : classes.offline}`}>
              {connection.online === undefined ? null : connection.online ? <WifiIcon fontSize="small" /> : <WifiOffIcon fontSize="small" />}
              {connection.online === undefined ? t("unknownState") : connection.online ? t("online") : t("offline")}
              {connectionContract && (
                <span className={classes.muted} style={{ fontWeight: 400 }}>
                  · {t("contract")} {connectionContract.code || connectionContract.id}
                </span>
              )}
            </div>
            {connection.planName && (
              <Typography className={classes.line} style={{ marginBottom: 2 }}>
                {connection.planName}
                {connection.pop ? <span className={classes.muted}> · {connection.pop}</span> : null}
              </Typography>
            )}
            <KVGroup
              classes={classes}
              title={t("groupConnection")}
              items={[
                { k: t("connType"), v: connection.connectionType },
                { k: t("login"), v: connection.login },
                {
                  k: t("ip"),
                  v: connection.ip ? (
                    <a href={`http://${connection.ip}`} target="_blank" rel="noopener noreferrer">
                      {connection.ip}
                    </a>
                  ) : undefined
                },
                {
                  k: t("wanIp"),
                  v:
                    connection.cpe && connection.cpe.wanIp && connection.cpe.wanIp !== connection.ip ? (
                      <a href={`http://${connection.cpe.wanIp}`} target="_blank" rel="noopener noreferrer">
                        {connection.cpe.wanIp}
                      </a>
                    ) : undefined
                },
                { k: t("mac"), v: connection.mac },
                {
                  k: t("nas"),
                  v: connection.nasIp ? `${connection.nasIp}${connection.nasPort ? ` (${connection.nasPort})` : ""}` : undefined
                },
                {
                  k: t("uptime"),
                  v:
                    connection.uptimeSeconds !== undefined && connection.uptimeSeconds !== null
                      ? formatUptime(connection.uptimeSeconds)
                      : undefined
                },
                {
                  k: t("sessionUsage"),
                  v:
                    connection.sessionDownloadBytes !== undefined || connection.sessionUploadBytes !== undefined
                      ? `↓ ${formatBytes(connection.sessionDownloadBytes)} · ↑ ${formatBytes(connection.sessionUploadBytes)}`
                      : undefined
                },
                {
                  k: t("lastConnection"),
                  v: connection.lastConnectionStart ? formatDateTime(connection.lastConnectionStart) : undefined
                },
                {
                  k: t("lastDisconnect"),
                  v: connection.lastConnectionEnd
                    ? `${formatDateTime(connection.lastConnectionEnd)}${connection.disconnectCause ? ` (${connection.disconnectCause})` : ""}`
                    : undefined
                }
              ]}
            />
            <KVGroup
              classes={classes}
              title={t("groupFiber")}
              items={[
                {
                  k: t("signal"),
                  v:
                    connection.opticalSignalDbm !== undefined && connection.opticalSignalDbm !== null ? (
                      <>
                        <span className={signalClass(connection.opticalSignalDbm, classes)}>
                          {connection.opticalSignalDbm} dBm ({signalLabel(connection.opticalSignalDbm)})
                        </span>
                        {connection.opticalTxDbm !== undefined && connection.opticalTxDbm !== null && (
                          <span className={classes.muted}> · TX {connection.opticalTxDbm} dBm</span>
                        )}
                      </>
                    ) : (
                      <span className={classes.muted}>{t("signalNotProvided")}</span>
                    )
                },
                {
                  k: t("olt"),
                  v: connection.olt ? `${connection.olt}${connection.onuPosition ? ` (${connection.onuPosition})` : ""}` : undefined
                },
                { k: t("onuSerial"), v: connection.onuSerial }
              ]}
            />
            {connection.cpe && (
              <KVGroup
                classes={classes}
                title={t("groupCpe")}
                items={[
                  {
                    k: t("equipment"),
                    v: connection.cpe.model
                      ? `${connection.cpe.model}${connection.cpe.firmwareVersion ? ` (fw ${connection.cpe.firmwareVersion})` : ""}`
                      : undefined
                  },
                  {
                    k: t("wifi"),
                    v:
                      connection.cpe.wifiSsid || connection.cpe.wifiSsid5
                        ? [
                            connection.cpe.wifiSsid
                              ? `${connection.cpe.wifiSsid}${connection.cpe.wifiEnabled === false ? ` (${t("wifiOff")})` : ""}`
                              : null,
                            connection.cpe.wifiSsid5
                              ? `${connection.cpe.wifiSsid5} 5G${connection.cpe.wifiEnabled5 === false ? ` (${t("wifiOff")})` : ""}`
                              : null
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        : undefined
                  },
                  { k: t("lanDevices"), v: connection.cpe.lanDeviceCount }
                ]}
              />
            )}
            {connection.connectionLog && connection.connectionLog.length > 1 && (
              <>
                <Typography className={classes.line} style={{ fontWeight: 600, marginTop: 4 }}>
                  {t("lastSessions")}:
                </Typography>
                {connection.connectionLog.slice(0, 4).map((s, i) => (
                  <Typography key={i} className={classes.line}>
                    • {formatDateTime(s.start)} → {s.end ? formatDateTime(s.end) : t("sessionOpen")}
                    {s.ip ? ` · ${s.ip}` : ""}
                    {s.end && s.cause ? ` (${s.cause})` : ""}
                  </Typography>
                ))}
              </>
            )}
            {connection.serviceOrders && connection.serviceOrders.length > 0 && (
              <>
                <Typography className={classes.line} style={{ fontWeight: 600, marginTop: 4 }}>
                  {t("serviceOrders")}:
                </Typography>
                {connection.serviceOrders.map(os => (
                  <Typography key={os.id} className={classes.line}>
                    • {os.reason || os.type || `#${os.id}`}
                    {os.status ? ` — ${os.status}` : ""}
                    {os.scheduledDate
                      ? ` (${t("osScheduled")} ${formatDate(os.scheduledDate)})`
                      : os.createdDate
                      ? ` (${t("osOpened")} ${formatDate(os.createdDate)})`
                      : ""}
                  </Typography>
                ))}
              </>
            )}
            {connection.notes && <Typography className={classes.muted}>{connection.notes}</Typography>}
          </>
        ) : (
          <Typography className={connectionError ? classes.warn : classes.muted}>{connectionError || t("unknownState")}</Typography>
        )}
      </Paper>

      {/* Faturas em aberto */}
      <Paper variant="outlined" className={classes.card}>
        <CardTitle
          icon={<ReceiptIcon />}
          action={
            invoices.length > 0 ? (
              <Button
                size="small"
                color="primary"
                disabled={!canSend || !!busy}
                startIcon={busy === "all" ? <CircularProgress size={12} /> : <SendIcon />}
                onClick={() => callTicket("send-all-invoices", null, "all")}
              >
                {t("sendAll")}
              </Button>
            ) : null
          }
        >
          {t("invoices")}
        </CardTitle>
        {canSend && invoices.length > 0 && (
          <FormControlLabel
            control={<Switch size="small" color="primary" checked={closeAfter} onChange={(e) => setCloseAfter(e.target.checked)} />}
            label={<Typography variant="caption">{t("closeAfterSend")}</Typography>}
          />
        )}
        {invoicesError && <Typography className={classes.error}>{invoicesError}</Typography>}
        {invoices.length === 0 ? (
          !invoicesError && <Typography className={classes.muted}>{t("noInvoices")}</Typography>
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("dueDate")}</TableCell>
                  <TableCell align="right">{t("amount")}</TableCell>
                  <TableCell>{t("status")}</TableCell>
                  <TableCell align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => {
                  const overdue = isOverdue(inv);
                  const hasPix = !!(inv.payment && inv.payment.pixCopiaECola);
                  const key = `${inv.contractId || ""}:${inv.id}`;
                  return (
                    <TableRow key={key}>
                      <TableCell>{formatDate(inv.dueDate)}</TableCell>
                      <TableCell align="right">{formatCurrency(inv.amount)}</TableCell>
                      <TableCell className={overdue ? classes.overdue : classes.upcoming}>{overdue ? t("overdue") : t("upcoming")}</TableCell>
                      <TableCell align="right">
                        <div className={classes.rowActions}>
                          <Tooltip title={t("sendInChat")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={t("sendInChat")}
                                disabled={!canSend || !!busy}
                                onClick={() => callTicket("send-invoice", { invoiceId: inv.id, contractId: inv.contractId, mode: "full" }, `inv:${key}`)}
                              >
                                {busy === `inv:${key}` ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t("pix")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={t("pix")}
                                disabled={!canSend || !!busy || (inv.payment && !hasPix)}
                                onClick={() => callTicket("send-invoice", { invoiceId: inv.id, contractId: inv.contractId, mode: "pix" }, `pix:${key}`)}
                              >
                                {busy === `pix:${key}` ? <CircularProgress size={14} /> : <AccountBalanceIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>

      {/* Ações */}
      <Paper variant="outlined" className={classes.card}>
        <CardTitle icon={<LockOpenIcon />}>{t("actions")}</CardTitle>
        <div className={classes.actionsRow}>
          {unblockable.length === 0 ? (
            <Tooltip title={t("trustUnblockHint")}>
              <span>
                <Button size="small" variant="outlined" disabled startIcon={<LockOpenIcon />}>
                  {t("trustUnblock")}
                </Button>
              </span>
            </Tooltip>
          ) : (
            unblockable.map(ct => (
              <Button
                key={ct.id}
                size="small"
                variant="outlined"
                color="primary"
                disabled={!!busy}
                startIcon={busy === "unblock" ? <CircularProgress size={12} /> : <LockOpenIcon />}
                onClick={() => setConfirmUnblock(ct)}
              >
                {t("trustUnblock")}
                {unblockable.length > 1 ? ` · ${ct.code || ct.id}` : ""}
              </Button>
            ))
          )}
        </div>
      </Paper>

      <Typography className={classes.footer}>
        {t("source")}: {overview.providerLabel} · {t("consultedAt")} {formatTime(overview.fetchedAt)}
      </Typography>

      <ConfirmationModal title={t("unlinkConfirmTitle")} open={confirmUnlink} onClose={setConfirmUnlink} onConfirm={handleUnlink}>
        {t("unlinkConfirm")}
      </ConfirmationModal>
      <ConfirmationModal
        title={t("trustUnblockConfirmTitle")}
        open={!!confirmUnblock}
        onClose={() => setConfirmUnblock(null)}
        onConfirm={() => confirmUnblock && handleUnblock(confirmUnblock.id)}
      >
        {t("trustUnblockConfirm", { contract: confirmUnblock ? confirmUnblock.code || confirmUnblock.id : "" })}
      </ConfirmationModal>
      <ErpWritebackDialog open={writebackOpen} onClose={() => setWritebackOpen(false)} ticketId={ticket?.id} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const ContactErpPanel = ({ contact, ticket }) => {
  const classes = useStyles();
  const contactId = contact && contact.id;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [password, setPassword] = useState("");
  // Incremented on every load / contact change / unmount so that a slow response
  // for a previous contact (or after unmount) never overwrites the current state.
  const requestSeq = useRef(0);

  const load = useCallback(
    async (pwd, silent) => {
      if (!contactId) return;
      const seq = ++requestSeq.current;
      const isCurrent = () => seq === requestSeq.current;
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const { data } = pwd
          ? await api.post(`/isp/contacts/${contactId}/erp/refresh`, { password: pwd })
          : await api.get(`/isp/contacts/${contactId}/erp`);
        if (!isCurrent()) return;
        setOverview(data);
        if (!data || !data.linked) {
          const res = await api.get("/isp/erp-integrations");
          if (!isCurrent()) return;
          setIntegrations(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!isCurrent()) return;
        toastError(err);
        setOverview(prev => prev || { linked: false });
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [contactId]
  );

  useEffect(() => {
    setOverview(null);
    setPassword("");
    load();
    return () => {
      // invalidates in-flight requests on contact change / unmount
      requestSeq.current += 1;
    };
  }, [load]);

  if (!contactId) return null;

  if (loading || !overview) {
    return (
      <div className={classes.root}>
        <Skeleton variant="rect" height={56} />
        <Skeleton variant="rect" height={90} />
        <Skeleton variant="rect" height={70} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {overview.linked ? (
        <LinkedView
          contact={contact}
          ticket={ticket}
          overview={overview}
          refreshing={refreshing}
          password={password}
          setPassword={setPassword}
          onRefresh={() => load(password, true)}
          onUnlinked={() => {
            setPassword("");
            load();
          }}
        />
      ) : (
        <LinkForm
          contact={contact}
          integrations={integrations}
          onLinked={(data, pwd) => {
            if (pwd) setPassword(pwd);
            if (data && data.linked) setOverview(data);
            else load(pwd);
          }}
        />
      )}
      <Divider style={{ visibility: "hidden" }} />
    </div>
  );
};

export default ContactErpPanel;
